import React, { useEffect, useState } from "react";
import { Button, Switch, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DataDialog } from "@app/components/atoms";
import type { UserSearchResult } from "./SearchUserTable";
import { apiPutUserAdminPermissions } from "@app/api";
import { AdminPermissions } from "@app/constants";

type Props = {
  open: boolean;
  user?: UserSearchResult | null;
  currentUserId: string;
  initialPermissions?: Partial<Record<AdminPermissions, boolean>>;
  loading?: boolean;
  onClose: () => void;
  onSave?: (permissions: Partial<Record<AdminPermissions, boolean>>) => void;
};

const PermissionsDialog: React.FC<Props> = ({
  open,
  user,
  currentUserId,
  initialPermissions,
  loading,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation();
  const [permissions, setPermissions] = useState<
    Partial<Record<AdminPermissions, boolean>>
  >({});
  const [saving, setSaving] = useState<boolean>(false);
  const isSelfUser = user?.userId === currentUserId;

  useEffect(() => {
    if (open) {
      setPermissions((prev) => ({
        ...(initialPermissions || {}),
        ...(isSelfUser && {
          [AdminPermissions.USER_MANAGE]: true,
        }),
      }));
    }
  }, [open, initialPermissions, isSelfUser]);

  const handleToggle = (permission: AdminPermissions, value: boolean) => {
    if (isSelfUser && permission === AdminPermissions.USER_MANAGE) {
      return;
    }

    setPermissions((prev) => ({ ...prev, [permission]: value }));
  };

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);

    try {
      const userId = user.userId;
      await apiPutUserAdminPermissions(userId, permissions);

      onSave && onSave(permissions);
      onClose();
    } catch (err) {
      console.error("Failed to save permissions:", err);
    } finally {
      setSaving(false);
    }
  };

  const displayName = `${user?.givenName || ""} ${user?.surname || ""}`.trim();

  return (
    <DataDialog
      open={open}
      onClose={onClose}
      title={displayName || t("permission.USER_SEARCH")}
      fullWidth
      dialogActions={
        <>
          <Button
            onClick={onClose}
            className="btn btn-no-border"
            style={{ width: "auto", marginRight: "0px" }}
          >
            {t("dialogReport.cancel")}
          </Button>
          <Button
            className="btn btn-primary"
            onClick={handleSave}
            style={{ width: "auto" }}
            disabled={loading || saving}
          >
            {t("formAction.save")}
          </Button>
        </>
      }
    >
      {user?.email && (
        <Typography variant="body2" gutterBottom>
          {user.email}
        </Typography>
      )}

      <ul
        className="cards bg-gray"
        style={{ borderRadius: 0, padding: 0, marginTop: 8 }}
      >
        {Object.values(AdminPermissions).map((perm) => {
          const isUserManageSelf =
            isSelfUser && perm === AdminPermissions.USER_MANAGE;

          return (
            <li key={perm} className="card-item">
              <div className="card-item-title flex-centered justify-content-between have-switch">
                <span
                  className="txt-card"
                  style={{ fontSize: 16, color: "#374151" }}
                >
                  {t(`permission.${perm}`)}
                </span>
                <div className="d-flex align-items-center">
                  <Switch
                    className={`btn-switch ${permissions[perm] ? "checked" : ""}`}
                    checked={!!permissions[perm]}
                    onChange={(e) => handleToggle(perm, e.target.checked)}
                    color="primary"
                    disabled={loading || saving || isUserManageSelf}
                    sx={
                      isUserManageSelf
                        ? {
                          opacity: 0.45,
                          filter: "grayscale(100%)",
                          cursor: "not-allowed",
                        }
                        : undefined
                    }
                  />
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </DataDialog>
  );
};

export default PermissionsDialog;
